
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Users, UserPlus, Mail, Trash2, RotateCcw, Shield, Lock, Eye } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useAdminUsers } from '@/hooks/useAdminUsers';
import { useToast } from '@/hooks/use-toast';

const CRMUsers = () => {
  const { user, userRole } = useAuth();
  const { users, loading, fetchUsers, inviteUser, updateUserRole, resetUserPassword, deleteUser, setUserPassword, impersonateUser } = useAdminUsers();
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'super_admin'>('admin');
  const [inviteLoading, setInviteLoading] = useState(false);
  const { toast } = useToast();

  const [pwDialogOpen, setPwDialogOpen] = useState(false);
  const [pwTargetUserId, setPwTargetUserId] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwLoading, setPwLoading] = useState(false);

  useEffect(() => {
    if (userRole === 'super_admin') {
      fetchUsers();
    }
  }, [userRole]);

  if (userRole !== 'super_admin') {
    return (
      <div className="container mx-auto py-6">
        <div className="text-center">
          <Shield className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-2xl font-bold mb-2">Toegang geweigerd</h2>
          <p className="text-muted-foreground">
            Je hebt super_admin rechten nodig om gebruikers te beheren.
          </p>
        </div>
      </div>
    );
  }

  const handleInviteUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;

    setInviteLoading(true);
    const success = await inviteUser(inviteEmail.trim(), inviteRole);
    if (success) {
      setInviteEmail('');
      setInviteRole('admin');
    }
    setInviteLoading(false);
  };

  const handleRoleChange = async (userId: string, newRole: 'admin' | 'super_admin') => {
    await updateUserRole(userId, newRole);
  };

  const handleResetPassword = async (email: string) => {
    await resetUserPassword(email);
  };

  const handleDeleteUser = async (userId: string) => {
    await deleteUser(userId);
  };

  const openSetPasswordDialog = (userId: string) => {
    setPwTargetUserId(userId);
    setNewPassword('');
    setConfirmPassword('');
    setPwDialogOpen(true);
  };

  const handleSetPassword = async () => {
    if (!pwTargetUserId) return;
    if (newPassword.length < 6) {
      toast({ title: 'Wachtwoord te kort', description: 'Minimaal 6 karakters.', variant: 'destructive' });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast({ title: 'Wachtwoorden komen niet overeen', description: 'Controleer je invoer.', variant: 'destructive' });
      return;
    }
    setPwLoading(true);
    const ok = await setUserPassword(pwTargetUserId, newPassword);
    setPwLoading(false);
    if (ok) setPwDialogOpen(false);
  };

  const getInitials = (email: string, displayName?: string | null) => {
    if (displayName) {
      return displayName.split(' ').map(n => n[0]).join('').toUpperCase();
    }
    return email.substring(0, 2).toUpperCase();
  };

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'super_admin':
        return 'destructive';
      case 'admin':
        return 'default';
      default:
        return 'secondary';
    }
  };

  const getRoleDisplayName = (role: string) => {
    switch (role) {
      case 'super_admin':
        return 'Super Admin';
      case 'admin':
        return 'Beheerder';
      default:
        return 'Gebruiker';
    }
  };

  return (
    <div className="container mx-auto py-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Users className="h-6 w-6" />
          <h1 className="text-3xl font-bold">Gebruikersbeheer</h1>
        </div>
      </div>

      <div className="grid gap-6">
        {/* Invite User Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5" />
              Nieuwe gebruiker uitnodigen
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleInviteUser} className="flex gap-4 items-end">
              <div className="flex-1">
                <Label htmlFor="inviteEmail">E-mailadres</Label>
                <Input
                  id="inviteEmail"
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="gebruiker@voorbeeld.nl"
                  required
                />
              </div>
              <div className="w-48">
                <Label htmlFor="inviteRole">Rol</Label>
                <Select value={inviteRole} onValueChange={(value: 'admin' | 'super_admin') => setInviteRole(value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Beheerder</SelectItem>
                    <SelectItem value="super_admin">Super Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" disabled={inviteLoading}>
                {inviteLoading ? 'Uitnodigen...' : 'Uitnodigen'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Users List */}
        <Card>
          <CardHeader>
            <CardTitle>Alle gebruikers ({users.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                <p className="mt-2 text-muted-foreground">Gebruikers laden...</p>
              </div>
            ) : users.length === 0 ? (
              <div className="text-center py-8">
                <Users className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Geen gebruikers gevonden</p>
              </div>
            ) : (
              <div className="space-y-4">
                {users.map((adminUser) => (
                  <div key={adminUser.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center space-x-4">
                      <Avatar className="h-12 w-12">
                        <AvatarImage src={adminUser.profile?.avatar_url || ''} />
                        <AvatarFallback>
                          {getInitials(adminUser.email, adminUser.profile?.display_name)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium">
                            {adminUser.profile?.display_name || adminUser.email}
                          </p>
                          <Badge variant={getRoleBadgeVariant(adminUser.role)}>
                            {getRoleDisplayName(adminUser.role)}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">{adminUser.email}</p>
                        <div className="flex gap-4 text-xs text-muted-foreground mt-1">
                          <span>Aangemaakt: {new Date(adminUser.created_at).toLocaleDateString('nl-NL')}</span>
                          {adminUser.last_sign_in_at && (
                            <span>Laatste login: {new Date(adminUser.last_sign_in_at).toLocaleDateString('nl-NL')}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {/* Role selector */}
                      <Select
                        value={adminUser.role}
                        onValueChange={(value: 'admin' | 'super_admin') => handleRoleChange(adminUser.id, value)}
                        disabled={adminUser.id === user?.id} // Can't change own role
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Beheerder</SelectItem>
                          <SelectItem value="super_admin">Super Admin</SelectItem>
                        </SelectContent>
                      </Select>

                      {/* Impersonate user */}
                      {adminUser.id !== user?.id && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            const loginUrl = await impersonateUser(adminUser.id);
                            if (loginUrl) {
                              window.open(loginUrl, '_blank');
                            }
                          }}
                          title="Inloggen als deze gebruiker"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      )}

                      {/* Reset password */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleResetPassword(adminUser.email)}
                        title="Wachtwoord reset versturen"
                      >
                        <RotateCcw className="h-4 w-4" />
                      </Button>

                      {/* Set password manually */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openSetPasswordDialog(adminUser.id)}
                        title="Wachtwoord handmatig instellen"
                      >
                        <Lock className="h-4 w-4" />
                      </Button>

                      {/* Delete user */}
                      {adminUser.id !== user?.id && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="sm" title="Gebruiker verwijderen">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Gebruiker verwijderen</AlertDialogTitle>
                              <AlertDialogDescription>
                                Weet je zeker dat je {adminUser.email} wilt verwijderen? 
                                Deze actie kan niet ongedaan worden gemaakt.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Annuleren</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDeleteUser(adminUser.id)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                Verwijderen
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Manual password dialog */}
      <Dialog open={pwDialogOpen} onOpenChange={setPwDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Wachtwoord handmatig instellen</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="new-pw">Nieuw wachtwoord</Label>
              <Input id="new-pw" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="confirm-pw">Bevestig wachtwoord</Label>
              <Input id="confirm-pw" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setPwDialogOpen(false)}>Annuleren</Button>
              <Button onClick={handleSetPassword} disabled={pwLoading}>{pwLoading ? 'Opslaan...' : 'Opslaan'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CRMUsers;
